import { useNavigate } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipTrigger,
} from '@workspace/ui/components/hybrid-tooltip';
import { useIsMobile } from '@workspace/ui/hooks/use-mobile';
import { cn } from '@workspace/ui/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CreditCard,
  MessageSquareWarning,
  Receipt,
  ReceiptText,
  SquarePlus,
  UserPlus,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { ChatInitial } from '~/components/chat/initial';
import { ChatInput } from '~/components/chat/input';
import { ChatMessages } from '~/components/chat/messages';
import { useChat } from '~/hooks/use-chat/use-chat';
import { m } from '~/paraglide/messages.js';

const getBusinessShortcuts = () => [
  {
    label: m['chat.shortcuts.top_up_label'](),
    icon: SquarePlus,
    message: m['chat.shortcuts.top_up_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.add_collaborator_label'](),
    icon: UserPlus,
    message: m['chat.shortcuts.add_collaborator_message'](),
    enabled: true,
  },
  {
    label: m['chat.shortcuts.report_news_label'](),
    icon: MessageSquareWarning,
    message: m['chat.shortcuts.report_news_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.generate_payroll_label'](),
    icon: ReceiptText,
    message: m['chat.shortcuts.generate_payroll_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.pay_payroll_label'](),
    icon: Receipt,
    message: m['chat.shortcuts.pay_payroll_message'](),
    enabled: false,
  },
];

const getNonBusinessShortcuts = () => [
  {
    label: m['chat.shortcuts.create_card_label'](),
    icon: CreditCard,
    message: m['chat.shortcuts.create_card_message'](),
    enabled: true,
  },
  {
    label: m['chat.shortcuts.top_up_label'](),
    icon: SquarePlus,
    message: m['chat.shortcuts.top_up_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.report_news_label'](),
    icon: MessageSquareWarning,
    message: m['chat.shortcuts.report_news_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.generate_report_label'](),
    icon: ReceiptText,
    message: m['chat.shortcuts.generate_report_message'](),
    enabled: false,
  },
  {
    label: m['chat.shortcuts.check_movements_label'](),
    icon: Receipt,
    message: m['chat.shortcuts.check_movements_message'](),
    enabled: false,
  },
];

type ChatProps = {
  maxWidth?: '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
};

const maxWidthClasses = {
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

export function Chat({ maxWidth = '3xl' }: ChatProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    messages,
    input,
    isLoading,
    handleInputChange,
    attachedFiles,
    handleFileSelect,
    removeFile,
    status,
    handleToolCall,
    thinkings,
    currentMessageHasToolCall,
  } = useChat({});

  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useIsMobile();

  const allShortcuts =
    user?.metadata.usage === 'business'
      ? getBusinessShortcuts()
      : getNonBusinessShortcuts();

  const quickShortcuts = isMobile ? allShortcuts.slice(0, 4) : allShortcuts;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (e.dataTransfer.files) {
        handleFileSelect(e.dataTransfer.files);
      }
    },
    [handleFileSelect],
  );

  const handleSubmit = async () => {
    if (isMobile) {
      await navigate({
        to: '/chat',
        search: { q: input.value },
      });
      return;
    }

    const baseUrl = window.location.origin;

    const url = new URL('/chat', baseUrl);

    url.searchParams.set('q', input.value);
    input.value = '';

    window.open(url, '_blank');
  };

  const handleShortcutClick = async (message: string) => {
    if (isMobile) {
      await navigate({
        to: '/chat',
        search: { q: message },
      });
      return;
    }

    const baseUrl = window.location.origin;
    const url = new URL('/chat', baseUrl);

    url.searchParams.set('q', message);

    window.open(url, '_blank');
  };

  return (
    <div
      className="relative flex flex-1 flex-col justify-between h-full w-full"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="application"
      aria-label="Chat area with file drop support"
    >
      <motion.div
        key="chat"
        className={cn(
          'flex-1 h-full flex flex-col items-center transition-all duration-500 ease-in-out justify-center',
        )}
      >
        <div
          className={cn(
            'w-full flex flex-col items-center gap-6 px-0 md:px-2',
            maxWidthClasses[maxWidth],
          )}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key="messages"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="w-full flex-1"
            >
              <ChatInitial />
              <ChatMessages
                status={status}
                messages={messages}
                handleAction={handleToolCall}
                thinkings={thinkings}
              />
            </motion.div>
          </AnimatePresence>

          <div className="w-full border-2 border-input rounded-lg p-4">
            <ChatInput
              isLoading={isLoading}
              isDragging={isDragging}
              input={input}
              attachedFiles={attachedFiles}
              handleFileSelect={handleFileSelect}
              handleSubmit={handleSubmit}
              removeFile={removeFile}
              handleInputChange={handleInputChange}
              currentMessageHasToolCall={currentMessageHasToolCall}
              status={status}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-4">
              {quickShortcuts.map((shortcut, index) =>
                shortcut.enabled ? (
                  <Button
                    key={index}
                    className="flex flex-col items-center justify-center p-4 h-auto"
                    variant="outline"
                    onClick={() => handleShortcutClick(shortcut.message)}
                  >
                    <shortcut.icon className="mb-1 !w-6 !h-6" />
                    <span className="text-center text-sm leading-tight hyphens-auto break-words whitespace-normal">
                      {shortcut.label}
                    </span>
                  </Button>
                ) : (
                  <HybridTooltip key={index}>
                    <HybridTooltipTrigger asChild>
                      <Button
                        className="flex flex-col items-center justify-center p-4 h-auto opacity-60 cursor-not-allowed"
                        variant="outline"
                      >
                        <shortcut.icon className="mb-1 !w-6 !h-6" />
                        <span className="text-center text-sm leading-tight hyphens-auto break-words whitespace-normal">
                          {shortcut.label}
                        </span>
                      </Button>
                    </HybridTooltipTrigger>
                    <HybridTooltipContent>
                      <p>{m['chat.shortcuts.coming_soon_tooltip']()}</p>
                    </HybridTooltipContent>
                  </HybridTooltip>
                ),
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
